import React, { useEffect, useMemo, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { Calendar, CalendarChangeParams } from 'primereact/calendar'
import useToastr from '../../../hooks/useToastr'
import classes from './ManilaCoeTrackerForm.module.scss'
import { ConfirmDialog } from 'primereact/confirmdialog'
import { faCheck, faPencil, faTimes, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from 'primereact/button'
import ProjectService from '../../../services/ProjectService'
import { ProgressSpinner } from 'primereact/progressspinner'
import {
  IManilaCoeTrackerFormProps,
  ITrackerEntry,
  ITrackerFormState,
  IPendingDeleteState,
  TrackerColumn,
} from '../shared/interfaces/manila-coe-tracker'
import { ManilaCoeTrackerDetailsModel } from '../../../shared/models/service-models/ManilaCoeTrackerDetailsModel'
import { authProvider } from '../../../providers/authProvider'
import { v4 as uuidv4 } from 'uuid'
import {
  formatDateForInput,
  normalizeDateInput,
  parseDateInput,
} from '../../../shared/date-helper/DateHelper'
import { InputTextarea } from 'primereact/inputtextarea'

const DATE_INPUT_PATTERN = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/
const buildTimestamp = (date: string, time: string) => `${date} | ${time}`
type SortDirection = 'ascending' | 'descending' | null

const ManilaCoeTrackerForm = ({
  visible,
  onHide,
  handleUpdateTargetOrderline,
  e2eProjectLineId,
  lineNumber,
  orderNumber,
  hasComment,
}: IManilaCoeTrackerFormProps) => {
  const { showSuccess, showError, showInfo } = useToastr()
  const currentUser = authProvider.getAccountInfo()?.account
  const userEmail = currentUser?.idToken.email ?? ''

  const initialPersistedFormState: ITrackerFormState = {
    orderNumber: orderNumber,
    targetSubmissionDate: 'mm/dd/yyyy',
    lineNumber: lineNumber,
    typeOfSample: '',
    applicationNumber: '',
    handlerNote: '',
    pmComment: '',
  }

  const [draftForm, setDraftForm] = useState<ITrackerFormState>(initialPersistedFormState)
  const [defaultValue, setDefaultValue] = useState<ITrackerFormState>()

  const [handlerNotes, setHandlerNotes] = useState<ITrackerEntry[]>([])
  const [pmComments, setPmComments] = useState<ITrackerEntry[]>([])
  const [sortDirections, setSortDirections] = useState<Record<TrackerColumn, SortDirection>>({
    handler: null,
    pm: null,
  })
  const [editingEntry, setEditingEntry] = useState<{
    column: TrackerColumn
    id: string
    text: string
  } | null>(null)
  const [pendingDelete, setPendingDelete] = useState<IPendingDeleteState | null>(null)
  const [dateError, setDateError] = useState('')
  const [calendarKey, setCalendarKey] = useState(0)
  const [completedPlanningWOEntry, setCompletedPlanningWOEntry] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState({
    isSubmitting: false,
    isFetching: false,
    isDeleting: false,
    isEditing: false,
  })

  const parsedTargetSubmissionDate = parseDateInput(
    draftForm.targetSubmissionDate,
    DATE_INPUT_PATTERN
  )
  const isDateValid = !!parsedTargetSubmissionDate

  const isFormDirty =
    !!defaultValue &&
    (draftForm.targetSubmissionDate !== defaultValue.targetSubmissionDate ||
      draftForm.typeOfSample !== defaultValue.typeOfSample ||
      draftForm.applicationNumber !== defaultValue.applicationNumber ||
      !!draftForm.handlerNote.trim() ||
      !!draftForm.pmComment.trim())

  const hasDraftChanges = isFormDirty || completedPlanningWOEntry

  const hasAnyInput = useMemo(
    () =>
      [
        draftForm.targetSubmissionDate,
        draftForm.typeOfSample,
        draftForm.applicationNumber,
        draftForm.handlerNote.trim(),
        draftForm.pmComment.trim(),
      ].some(value => value.length > 0),
    [draftForm]
  )

  const isActionButtonDisabled =
    !hasAnyInput || isLoading.isSubmitting || isLoading.isFetching || isLoading.isEditing

  const resetDraftState = () => {
    setDraftForm(initialPersistedFormState)
    setDefaultValue(undefined)
    setPendingDelete(null)
    setEditingEntry(null)
    setDateError('')
    setCalendarKey(previousValue => previousValue + 1)
  }

  const closeDialog = () => {
    resetDraftState()
    onHide()
  }

  const updateDraftField = (field: keyof ITrackerFormState, value: string) => {
    setDraftForm(currentValue => ({
      ...currentValue,
      [field]: value,
    }))
  }

  const handleDateInput = (event: React.FormEvent<HTMLInputElement>) => {
    const nextValue = normalizeDateInput(event.currentTarget.value, draftForm.targetSubmissionDate)
    event.currentTarget.value = nextValue

    const parsedDate = parseDateInput(nextValue, DATE_INPUT_PATTERN)

    updateDraftField('targetSubmissionDate', nextValue)

    if (!nextValue || parsedDate) {
      setDateError('')
    }
  }

  const handleDateSelect = (event: CalendarChangeParams) => {
    const nextValue = event.value

    if (!(nextValue instanceof Date)) return

    updateDraftField('targetSubmissionDate', formatDateForInput(nextValue))
    setDateError('')
  }

  const handleCancel = () => {
    closeDialog()
    showInfo('Update Cancelled', "Changes we're discarded. No Update has been saved.")
  }

  const formatDateWithTimestamp = (date: Date = new Date()) => {
    const dateLabel = date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
    const timeLabel = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })

    return buildTimestamp(dateLabel, timeLabel)
  }

  const upsertEntry = (entries: ITrackerEntry[], text: string, id?: string): ITrackerEntry[] => {
    if (!text.trim()) return entries

    const createdAt = new Date()

    return [
      {
        id: id ?? uuidv4(),
        author: userEmail,
        createdAt: formatDateWithTimestamp(createdAt),
        createdAtTimestamp: createdAt.getTime(),
        text,
      },
      ...entries,
    ]
  }

  const toggleSortDirection = (column: TrackerColumn) => {
    setSortDirections(currentValue => ({
      ...currentValue,
      [column]:
        currentValue[column] === null || currentValue[column] === 'descending'
          ? 'ascending'
          : 'descending',
    }))
  }

  const handleUpdate = async () => {
    setIsLoading(currentValue => ({ ...currentValue, isSubmitting: true }))

    try {
      if (!hasAnyInput || !isDateValid) {
        if (!isDateValid) {
          setDateError('Enter a valid date in MM/DD/YYYY format.')
        }
        throw new Error()
      }

      const response = await postManilaCoeTrackerData()
      if (response.isSuccess) {
        const newHandlerNote = response.data?.handlerNotes?.[0]
        const newComment = response.data?.pmComments?.[0]

        if (newHandlerNote) {
          setHandlerNotes(currentValue =>
            upsertEntry(currentValue, draftForm.handlerNote, newHandlerNote.id)
          )
        }

        if (newComment) {
          setPmComments(currentValue =>
            upsertEntry(currentValue, draftForm.pmComment, newComment.id)
          )
        }

        const savedForm = {
          ...draftForm,
          handlerNote: '',
          pmComment: '',
        }

        setDraftForm(savedForm)
        setDefaultValue(savedForm)
        setCompletedPlanningWOEntry(false)
        setDateError('')

        showSuccess('Update Successful', 'Your changes have been saved successfully.')
      } else {
        showError(
          'Update Failed',
          'Unable to update the COE report additional fields. Please try again.'
        )
      }

      if (!hasComment) handleUpdateTargetOrderline()
    } catch (error) {
      showError(
        'Update Failed',
        'Unable to update the COE report additional fields. Please try again.'
      )
    } finally {
      setIsLoading(currentValue => ({ ...currentValue, isSubmitting: false }))
    }
  }

  const handleEditEntry = (column: TrackerColumn, entry: ITrackerEntry) => {
    setIsLoading(currentValue => ({ ...currentValue, isEditing: true }))
    if (entry.author.trim().toLowerCase() !== userEmail.trim().toLowerCase()) return

    setEditingEntry({ column, id: entry.id, text: entry.text })
  }

  const handleSaveEdit = async () => {
    if (!editingEntry?.text.trim()) return

    try {
      const update = {
        id: editingEntry.id,
        ...(editingEntry.column === 'handler'
          ? { handlerNote: editingEntry.text.trim() }
          : { pmComment: editingEntry.text.trim() }),
      }
      const response = await ProjectService.updateCoeTrackerDetails(update)

      if (!response?.isSuccess) {
        showError('Update Failed', 'There was an error updating the note/comment.')
        return
      }

      const updateEntry = (entry: ITrackerEntry) =>
        entry.id === editingEntry.id
          ? { ...entry, text: editingEntry.text.trim(), edited: true }
          : entry

      if (editingEntry.column === 'handler') {
        setHandlerNotes(currentValue => currentValue.map(updateEntry))
      } else {
        setPmComments(currentValue => currentValue.map(updateEntry))
      }

      setEditingEntry(null)
      showSuccess('Update Successful', 'Your changes have been saved successfully.')
    } catch (error) {
      showError('Update Failed', 'There was an error updating the note/comment.')
    } finally {
      setIsLoading(currentValue => ({ ...currentValue, isEditing: false }))
    }
  }

  const handleDeleteEntry = async () => {
    if (!pendingDelete) return

    setIsLoading(currentValue => ({ ...currentValue, isDeleting: true }))

    try {
      const response = await ProjectService.deleteCoeTrackerDetails(pendingDelete.entryId)

      if (response?.isSuccess) {
        if (pendingDelete.column === 'handler') {
          setHandlerNotes(currentValue =>
            currentValue?.filter(entry => entry.id !== pendingDelete.entryId)
          )
        }

        if (pendingDelete.column === 'pm') {
          setPmComments(currentValue =>
            currentValue?.filter(entry => entry.id !== pendingDelete.entryId)
          )
        }
      }
    } catch (error) {
      showError('Delete Failed', 'There was an error deleting the note/comment.')
    } finally {
      setIsLoading(currentValue => ({ ...currentValue, isDeleting: false }))
      setPendingDelete(null)
      showSuccess(
        'Deleted Successfully',
        'Note/Comment was deleted successfully and is no longer visible.'
      )
    }
  }

  const renderTextField = (
    id: string,
    label: string,
    value: string,
    onChange: (value: string) => void,
    options?: { readOnly?: boolean }
  ) => (
    <label className={classes['field-group']} htmlFor={id}>
      <span className={classes['field-label']}>{label}</span>
      <input
        id={id}
        className={classes['text-input']}
        value={value}
        readOnly={options?.readOnly}
        onChange={event => onChange(event.target.value)}
      />
    </label>
  )

  const renderTextAreaField = (
    id: string,
    label: string,
    value: string,
    onChange: (value: string) => void
  ) => (
    <label className={classes['field-group']} htmlFor={id}>
      <span className={classes['field-label']}>{label}</span>
      <textarea
        id={id}
        className={classes['text-area']}
        value={value}
        onChange={event => onChange(event.target.value)}
      />
    </label>
  )

  const renderSpinner = (width = '35px', height = '35px') => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
      <ProgressSpinner style={{ width, height }} strokeWidth="5" animationDuration="1s" />
    </div>
  )

  const renderEntryColumn = (title: string, column: TrackerColumn, entries: ITrackerEntry[]) => {
    const sortDirection = sortDirections[column]
    const sortedEntries = [...entries].sort((firstEntry, secondEntry) => {
      const difference = firstEntry.createdAtTimestamp - secondEntry.createdAtTimestamp
      return sortDirection === 'ascending' ? difference : -difference
    })

    return (
      <>
        {isLoading.isFetching && <>{renderSpinner()}</>}
        {!isLoading.isFetching && (
          <section className={classes['entry-column']}>
            <div className={classes['entry-column-header']}>
              <span>
                {title} ({entries.length})
              </span>
              <button
                type="button"
                className={classes['sort-button']}
                aria-label={`Sort ${title} ${
                  sortDirection === 'ascending' ? 'newest first' : 'oldest first'
                }`}
                title={
                  sortDirection
                    ? `Sorted ${sortDirection === 'descending' ? 'newest' : 'oldest'} first`
                    : 'Sorted newest first'
                }
                onClick={() => toggleSortDirection(column)}
              >
                <i
                  className={`pi ${
                    sortDirection === null
                      ? 'pi-sort-alt'
                      : sortDirection === 'descending'
                        ? 'pi-sort-amount-down'
                        : 'pi-sort-amount-up'
                  } ${classes['entry-column-icon']}`}
                  aria-hidden="true"
                />
              </button>
            </div>

            <div className={classes['entry-list']}>
              {entries.length === 0 ? (
                <p className={classes['empty-message']}>
                  {column === 'handler' ? "No Handler's note found." : "No PM's comment found."}
                </p>
              ) : (
                sortedEntries.map(entry => (
                  <article key={entry.id} className={classes['entry-card']}>
                    <div className={classes['entry-actions']}>
                      {editingEntry?.id === entry.id && editingEntry.column === column ? (
                        <>
                          <button
                            type="button"
                            className={classes['icon-button']}
                            aria-label="Save changes"
                            onClick={handleSaveEdit}
                            disabled={isLoading.isSubmitting || !editingEntry.text.trim()}
                            style={{ color: '#006700' }}
                          >
                            <FontAwesomeIcon icon={faCheck} size="xs" />
                          </button>
                          <button
                            type="button"
                            className={classes['icon-button']}
                            aria-label="Discard changes"
                            onClick={() => setEditingEntry(null)}
                            disabled={isLoading.isSubmitting}
                            style={{ color: '#de0000' }}
                          >
                            <FontAwesomeIcon icon={faTimes} size="xs" />
                          </button>
                        </>
                      ) : (
                        <>
                          {entry.author.trim().toLowerCase() === userEmail.trim().toLowerCase() && (
                            <>
                              <button
                                type="button"
                                className={classes['icon-button']}
                                aria-label={`Edit ${title}`}
                                onClick={() => handleEditEntry(column, entry)}
                                disabled={editingEntry !== null}
                              >
                                <FontAwesomeIcon icon={faPencil} size="xs" />
                              </button>
                              <button
                                type="button"
                                className={classes['icon-button']}
                                aria-label={`Delete ${title}`}
                                onClick={() => setPendingDelete({ column, entryId: entry.id })}
                              >
                                <FontAwesomeIcon icon={faTrash} size="xs" />
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>

                    <div className={classes['entry-author-row']}>
                      <strong>{entry.author}</strong>
                      {entry.edited ? (
                        <span className={classes['edited-tag']}>(Edited)</span>
                      ) : null}
                    </div>
                    <div className={classes['entry-meta']}>{entry.createdAt}</div>

                    {editingEntry?.id === entry.id && editingEntry.column === column ? (
                      <InputTextarea
                        className={classes['edit-text-area']}
                        value={editingEntry.text}
                        onChange={(event: any) =>
                          setEditingEntry(currentValue =>
                            currentValue ? { ...currentValue, text: event.target.value } : null
                          )
                        }
                        autoFocus
                      />
                    ) : (
                      <p className={classes['entry-text']}>{entry.text}</p>
                    )}
                  </article>
                ))
              )}
            </div>
          </section>
        )}
      </>
    )
  }

  const renderFooter = () => (
    <div className={classes['dialog-footer']}>
      <Button className={classes['close-button']} onClick={closeDialog} label="CLOSE" />
    </div>
  )

  const confirmDeleteDialogBody = () => {
    return (
      <>
        <div className={`${classes['confirmation-content']} custom-confirm-dialog-body my-5`}>
          <div>Are you sure you want to delete this note/comment?</div>
          <div>This action cannot be undone.</div>
        </div>
      </>
    )
  }

  const confirmDeleteDialogFooter = () => {
    return (
      <>
        <div className="custom-confirm-dialog-footer">
          <Button
            className={`-medium ${classes['update-button']}`}
            label="YES"
            onClick={handleDeleteEntry}
            loading={isLoading.isDeleting}
          />
          <Button
            className={classes['cancel-button']}
            label="NO"
            onClick={() => setPendingDelete(null)}
            disabled={isLoading.isDeleting}
          />
        </div>
      </>
    )
  }

  const fetchManilaCoeTrackerData = async () => {
    setIsLoading(currentValue => ({ ...currentValue, isFetching: true }))
    const response = await ProjectService.getManilaCoeTrackerDetails(e2eProjectLineId ?? '')

    if (response.data) {
      const targetSubmissionDate = response.data.targetSubmissionDate ?? 'mm/ddd/yyyy'
      const typeOfSample = response.data.typesOfSample ?? ''
      const applicationNumber = response.data.applicationNumber ?? ''

      const validDate = parseDateInput(targetSubmissionDate, DATE_INPUT_PATTERN)

      setDefaultValue({
        orderNumber: orderNumber,
        targetSubmissionDate: targetSubmissionDate,
        lineNumber: lineNumber,
        typeOfSample: typeOfSample,
        applicationNumber: applicationNumber,
        handlerNote: '',
        pmComment: '',
      })

      setDraftForm(currentValue => ({
        ...currentValue,
        targetSubmissionDate,
        typeOfSample,
        applicationNumber,
      }))

      const fetchedHandlerNotes = response.data.handlerNotes ?? []
      const fetchedPmComments = response.data.pmComments ?? []

      if (fetchedHandlerNotes.length === 0 && fetchedPmComments.length === 0 && !!validDate) {
        setCompletedPlanningWOEntry(true)
      }

      const mappedNotes: ITrackerEntry[] = fetchedHandlerNotes?.map(note => {
        const createdAt = new Date(note.createdDateUtc ?? new Date())

        return {
          id: note.id,
          author: note.createdBy ?? '',
          createdAt: formatDateWithTimestamp(createdAt),
          createdAtTimestamp: createdAt.getTime(),
          edited: note.isUpdated,
          text: note.handlerNote ?? '',
        }
      })

      const mappedComments: ITrackerEntry[] = fetchedPmComments?.map(comment => {
        const createdAt = new Date(comment.createdDateUtc ?? new Date())

        return {
          id: comment.id,
          author: comment.createdBy ?? '',
          createdAt: formatDateWithTimestamp(createdAt),
          createdAtTimestamp: createdAt.getTime(),
          edited: comment.isUpdated,
          text: comment.pmComment ?? '',
        }
      })

      const nextHandlerNotes = upsertEntry(mappedNotes, draftForm.handlerNote)
      const nextPmComments = upsertEntry(mappedComments, draftForm.pmComment)

      setHandlerNotes(nextHandlerNotes)
      setPmComments(nextPmComments)
    } else {
      setDefaultValue(initialPersistedFormState)
    }
    setIsLoading(currentValue => ({ ...currentValue, isFetching: false }))
  }

  const postManilaCoeTrackerData = async () => {
    const model: ManilaCoeTrackerDetailsModel = {
      id: uuidv4(),
      e2EFlexProjectLineId: e2eProjectLineId ?? '',
      targetSubmissionDate: draftForm.targetSubmissionDate,
      typesOfSample: draftForm.typeOfSample,
      applicationNumber: draftForm.applicationNumber,
      handlerNotes: draftForm.handlerNote
        ? [
            {
              id: uuidv4(),
              handlerNote: draftForm.handlerNote,
              createdBy: userEmail,
            },
          ]
        : [],
      pmComments: draftForm.pmComment
        ? [
            {
              id: uuidv4(),
              pmComment: draftForm.pmComment,
              createdBy: userEmail,
            },
          ]
        : [],
    }

    const response = await ProjectService.postManilaCoeTrackerDetails(model)
    return response
  }

  useEffect(() => {
    setDraftForm(currentValue => ({
      ...currentValue,
      lineNumber,
      orderNumber,
    }))
  }, [lineNumber, orderNumber])

  useEffect(() => {
    if (!visible) return

    setHandlerNotes([])
    setPmComments([])
    setSortDirections({ handler: null, pm: null })
    setPendingDelete(null)
    setEditingEntry(null)
    setDateError('')
    setCalendarKey(previousValue => previousValue + 1)
    fetchManilaCoeTrackerData()
  }, [visible])

  return (
    <>
      <Dialog
        header="Manila COE Tracker Form"
        visible={visible}
        onHide={closeDialog}
        className={classes['dialog-container']}
        draggable={false}
        resizable={false}
        closable={true}
        footer={renderFooter()}
      >
        <div className={classes['dialog-body']}>
          <div className={classes['panel-shell']}>
            <section className={classes['form-column']}>
              <div className={classes['form-grid']}>
                {renderTextField(
                  'order-number',
                  'Order Number',
                  draftForm.orderNumber,
                  () => undefined,
                  {
                    readOnly: true,
                  }
                )}

                <label className={classes['field-group']} htmlFor="target-submission-date">
                  <span className={classes['field-label']}>
                    Target Submission Date <span className={classes['required']}>*</span>
                  </span>
                  <div className={classes['calendar-wrapper']}>
                    <Calendar
                      key={calendarKey}
                      inputId="target-submission-date"
                      value={
                        parseDateInput(draftForm.targetSubmissionDate, DATE_INPUT_PATTERN) ??
                        undefined
                      }
                      dateFormat="mm/dd/yy"
                      mask="99/99/9999"
                      keepInvalid={false}
                      showIcon
                      onChange={handleDateSelect}
                      onInput={handleDateInput}
                      className={classes['calendar-field']}
                      inputClassName={classes['text-input']}
                      placeholder="mm/dd/yyyy"
                      disabled={isLoading.isFetching}
                    />
                    {isLoading.isFetching && (
                      <div className={classes['calendar-spinner']}>
                        {renderSpinner('22px', '22px')}
                      </div>
                    )}
                  </div>
                  {dateError ? (
                    <small className={classes['validation-text']}>{dateError}</small>
                  ) : null}
                </label>

                <div className={classes['full-width']}>
                  {renderTextField(
                    'line-number',
                    'Line Number',
                    draftForm.lineNumber,
                    () => undefined,
                    {
                      readOnly: true,
                    }
                  )}
                </div>

                <div className={classes['full-width']}>
                  {renderTextField(
                    'type-of-sample',
                    'Type of Sample',
                    draftForm.typeOfSample,
                    value => updateDraftField('typeOfSample', value)
                  )}
                </div>

                <div className={classes['full-width']}>
                  {renderTextField(
                    'application-number',
                    'Application Number (Portal Submission)',
                    draftForm.applicationNumber,
                    value => updateDraftField('applicationNumber', value)
                  )}
                </div>

                <div className={classes['full-width']}>
                  {renderTextAreaField(
                    'handler-note',
                    "Handler's Note",
                    draftForm.handlerNote,
                    value => updateDraftField('handlerNote', value)
                  )}
                </div>

                <div className={classes['full-width']}>
                  {renderTextAreaField('pm-comment', "PM's Comment", draftForm.pmComment, value =>
                    updateDraftField('pmComment', value)
                  )}
                </div>
              </div>

              <div className={classes['action-row']}>
                <Button
                  type="button"
                  className={classes['cancel-button']}
                  onClick={handleCancel}
                  label="CANCEL"
                  disabled={isActionButtonDisabled || !isFormDirty}
                />

                <Button
                  className={classes['update-button']}
                  label="UPDATE"
                  onClick={handleUpdate}
                  disabled={isActionButtonDisabled || !isDateValid || !hasDraftChanges}
                  loading={isLoading.isSubmitting}
                />
              </div>
            </section>
          </div>
          <div className={classes['panel-shell']}>
            {renderEntryColumn("Handler's Note", 'handler', handlerNotes)}
          </div>
          <div className={classes['panel-shell']}>
            {renderEntryColumn("PM's Comment", 'pm', pmComments)}
          </div>
        </div>
      </Dialog>

      <ConfirmDialog
        style={{ width: '500px' }}
        header="Delete Confirmation"
        visible={!!pendingDelete}
        onHide={() => setPendingDelete(null)}
        className="custom-confirm-dialog"
        message={confirmDeleteDialogBody}
        footer={confirmDeleteDialogFooter}
      />
    </>
  )
}

export default ManilaCoeTrackerForm
